import React from "react";
import PATHS from "config/paths";

/* eslint-disable react-hooks/exhaustive-deps */

/**
 * Downloads a victim export limited to the selected columns.
 *
 * @param {string} token          Bearer token for the authed user.
 * @param {string[]} columns      Column keys to include (see EXPORT_COLUMNS backend).
 * @param {boolean} includeArchived Include soft-deleted/archived victims.
 * @param {"csv"|"xlsx"} exportFormat File format to download.
 * @param {boolean} shouldDownload Flip to true to trigger the download.
 */
export function useGetVictimExport (token, columns, includeArchived, exportFormat, shouldDownload) {
  const [error, setError] = React.useState();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isCompleted, setIsCompleted] = React.useState(false);

  React.useEffect(() => {
    if (token && shouldDownload && columns?.length > 0) {
      setIsDownloading(true);
      setIsCompleted(false);
      setError(undefined);

      const format = exportFormat === "xlsx" ? "xlsx" : "csv";
      const accept =
        format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv";

      const headers = new Headers();
      headers.append("Accept", accept);
      headers.append("Authorization", `Bearer ${token}`);

      const options = {
        method: "GET",
        headers: headers,
        redirect: "follow",
        cache: "no-cache",
      };

      let query = columns
        .map((column) => `columns[]=${encodeURIComponent(column)}`)
        .join("&");
      if (includeArchived) {
        query = `${query}&include_archived=true`;
      }
      if (format === "xlsx") {
        query = `${query}&export_format=xlsx`;
      }

      fetch(`${PATHS.apiVictims}/export_csv?${query}`, options)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Export failed (${response.status})`);
          }
          return response.blob();
        })
        .then((blob) => {
          // Use the response blob as-is so encoding bytes (BOM) are preserved.
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `victims-${(new Date().toJSON().slice(0, 10))}.${format}`
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
          setIsCompleted(true);
        })
        .catch((err) => setError(err))
        .finally(() => setIsDownloading(false));
    }
  }, [token, columns, includeArchived, exportFormat, shouldDownload]);

  return { isCompleted, downloadError: error, isDownloading };
}
